import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../utils/Axios.utils';

import useAuth from './useAuth';

// Helper to get auth token
// const getToken = () => {
//     const user = JSON.parse(localStorage.getItem('userLoggedIn'));
//     return user?.token;
// };

// --- API Functions ---

const fetchCandidates = async ({ page, search, status, supplierId, token }) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };

    let url = `/api/profile/supplier_candidate/${supplierId}?page=${page}&pageSize=10`;

    if (search) {
        url += `&search=${search}`;
    }

    if (status) {
        url += `&status=${status}`;
    }

    const response = await API.get(url, config);
    return response.data;
};

const deleteCandidate = async (profileId, token) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.delete(`/api/profile/${profileId}`, config);
    return response.data;
};

const fetchJobCodes = async (supplierId, token) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.get(`/api/job/jobcode?supplierId=${supplierId}`, config);
    return response.data;
};

const fetchJobSkills = async (jobCode, token) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.get(`/api/job/skills/${jobCode}`, config);
    return response.data;
};

const updateCandidate = async ({ profileId, formData, token }) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.put(`/api/profile/${profileId}`, formData, config);
    return response.data;
};

// --- Hooks ---

export const useGetCandidates = ({ page, search, status, supplierId }, options = {}) => {
    const { token } = useAuth();
    return useQuery({
        queryKey: ['candidates', supplierId, { page, search, status }],
        queryFn: () => fetchCandidates({ page, search, status, supplierId, token }),
        placeholderData: (previousData) => previousData, // Keep previous data while fetching new data to prevent flicker
        staleTime: 60 * 1000, // 1 minute
        enabled: !!supplierId,
        ...options,
    });
};

export const useDeleteCandidate = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();

    return useMutation({
        mutationFn: (profileId) => deleteCandidate(profileId, token),
        onSuccess: () => {
            // Invalidate candidates list to trigger refetch
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
        },
    });
};

export const useGetJobCodes = (supplierId) => {
    const { token } = useAuth();
    return useQuery({
        queryKey: ['jobCodes', supplierId],
        queryFn: () => fetchJobCodes(supplierId, token),
        enabled: !!supplierId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useGetJobSkills = (jobCode) => {
    const { token } = useAuth();
    return useQuery({
        queryKey: ['jobSkills', jobCode],
        queryFn: () => fetchJobSkills(jobCode, token),
        enabled: !!jobCode,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useUpdateCandidate = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();

    return useMutation({
        mutationFn: (variables) => updateCandidate({ ...variables, token }),
        onSuccess: (data, variables) => {
            // Invalidate specific candidate queries and the list
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
            // Also could invalidate detailed view if we had a query for that
        },
    });
};
